import { GroupedField } from "../../../grouping/grouping.module";
import { Action, Pricing } from "../../pricingListSlice";
import { uniq, flatten } from "lodash";

export function areEqualBySiteOrPortfolios(pricings: Pricing[]): boolean {
	const sites = pricings.map((p) =>
		p.site_pricings
			.map((site_pricing) => site_pricing.site.id)
			.sort()
			.join(",")
	);
	const equalBySites = sites.every((val) => val === sites[0]);

	const portfolios = pricings.map((p) => p.portfolio_id);
	const equalByPortfolios = portfolios.every(
		(val) => val && val === portfolios[0]
	);
	return equalBySites || equalByPortfolios;
}

export function areEqualByGroups(pricings: Pricing[]): boolean {
	const groupIds = pricings.map((p) => JSON.stringify(p.pricing_group_id));
	const uniqueByGroupIds = uniq(groupIds);
	return uniqueByGroupIds.length === groupIds.length;
}

export function areUniqueByPricingType(pricings: Pricing[]): boolean {
	const pricingTypeIds = pricings.map((p) =>
		JSON.stringify(p.pricing_type_id)
	);
	return uniq(pricingTypeIds).length === pricings.length;
}

export function areUniqueByCountry(pricings: Pricing[]): boolean {
	const countryIds = flatten(pricings.map((p) => p.countries_list));
	return uniq(countryIds).length === pricings.length;
}

export function areUniqueByProductFamily(pricings: Pricing[]): boolean {
	const pricingFamilies = flatten(pricings.map((p) => p.pricing_type.family));
	return uniq(pricingFamilies).length === pricings.length;
}

export function pricingsAreCompatibleWithSiteOrPortfolio(
	pricings: Pricing[],
	action: Action,
	selectedGroups: GroupedField[]
): boolean {
	if (action.by_site_or_portfolios) {
		return !selectedGroups.length && areEqualBySiteOrPortfolios(pricings);
	}
	return true;
}

export function pricingsAreCompatibleWithGroups(
	pricings: Pricing[],
	action: Action,
	selectedGroups: GroupedField[]
): boolean {
	if (action.is_unique_by_group) {
		return !selectedGroups.length && areEqualByGroups(pricings);
	}
	return true;
}

export function pricingsAreCompatibleWithPricingType(
	pricings: Pricing[],
	action: Action,
	selectedGroups: GroupedField[]
): boolean {
	if (action.is_unique_by_pricing_type) {
		return !selectedGroups.length && areUniqueByPricingType(pricings);
	}
	return true;
}

export function pricingsAreCompatibleWithCountry(
	pricings: Pricing[],
	action: Action,
	selectedGroups: GroupedField[]
): boolean {
	if (action.is_unique_by_country) {
		return !selectedGroups.length && areUniqueByCountry(pricings);
	}
	return true;
}

export function pricingsAreCompatibleWithProductFamily(
	pricings: Pricing[],
	action: Action,
	selectedGroups: GroupedField[]
): boolean {
	if (action.is_unique_by_product_family) {
		return !selectedGroups.length && areUniqueByProductFamily(pricings);
	}
	return true;
}

export function filterAction(
	action: Action,
	pricings: Pricing[],
	selectedGroups: GroupedField[]
): boolean {
	if (
		!pricingsAreCompatibleWithSiteOrPortfolio(
			pricings,
			action,
			selectedGroups
		)
	)
		return false;
	if (!pricingsAreCompatibleWithGroups(pricings, action, selectedGroups))
		return false;
	if (!pricingsAreCompatibleWithPricingType(pricings, action, selectedGroups))
		return false;
	if (!pricingsAreCompatibleWithCountry(pricings, action, selectedGroups))
		return false;
	if (
		!pricingsAreCompatibleWithProductFamily(
			pricings,
			action,
			selectedGroups
		)
	)
		return false;
	if (pricings.length > 1 && action.action === "ADD_PARTY") {
		return false;
	}

	return true;
}

export const filterActions = (
	pricings: Pricing[],
	actions: Action[],
	selectedGroups: GroupedField[]
): Action[] => {
	const res: Action[] = actions.filter((action) =>
		filterAction(action, pricings, selectedGroups)
	);
	return res;
};
